import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import SafeIcon from "../common/SafeIcon";
import VoiceInput from "./VoiceInput";
import AnimatedMarkdown from "./AnimatedMarkdown";
import PodcastModal from "./PodcastModal";

const {
  FiSend,
  FiUser,
  FiMessageCircle,
  FiLoader,
  FiCopy,
  FiCheck,
  FiTrash2,
  FiZap,
  FiBookmark,
  FiFileText,
  FiSearch,
  FiEdit3,
  FiMic,
} = FiIcons;

const ChatPanel = ({
  user,
  transcript,
  messages,
  onSendMessage,
  isLoading,
  error,
  onSaveSummary,
  onUpdateMessages,
  attachments = [],
}) => {
  const [inputMessage, setInputMessage] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [isPromptMenuOpen, setIsPromptMenuOpen] = useState(false);
  const [isPodcastModalOpen, setIsPodcastModalOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const menuRef = useRef(null);
  const textareaRef = useRef(null);

  const now = new Date();
  const activeAttachments = attachments.filter(
    (att) => att.useInChat && new Date(att.expiryDate) > now
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsPromptMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRef]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  const handleSendMessage = () => {
    if (!inputMessage.trim() || !transcript) return;
    onSendMessage(inputMessage);
    setInputMessage("");
  };

  const handleCopyMessage = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const handleSaveAsSummary = (content, id) => {
    onSaveSummary(content);
    setSavedId(id);
    setTimeout(() => setSavedId(null), 2000);
  };

  const handleDeleteMessagePair = (messageId, messageIndex) => {
    const userMessage = messages[messageIndex];
    const nextMessage = messages[messageIndex + 1];

    if (
      userMessage.type === "user" &&
      nextMessage &&
      nextMessage.type === "bot"
    ) {
      const userConfirmed = window.confirm(
        "คุณต้องการลบคำถามนี้และคำตอบที่เกี่ยวข้องหรือไม่?"
      );
      if (userConfirmed) {
        const updatedMessages = messages.filter(
          (msg) => msg.id !== userMessage.id && msg.id !== nextMessage.id
        );
        onUpdateMessages(updatedMessages);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleVoiceMessage = (voiceText) => setInputMessage(voiceText);

  const handleClearChat = () => {
    const userConfirmed1 = window.confirm(
      "คุณต้องการล้างการสนทนาทั้งหมดใช่หรือไม่?\n\nคำแนะนำ:\n- หากต้องการบันทึกคำตอบ: กดไอคอนรูปที่คั่นหนังสือ (Bookmark)\n- หากต้องการลบบางข้อความ: กดไอคอนรูปถังขยะที่คำถามของคุณ"
    );
    if (userConfirmed1) {
      const userConfirmed2 = window.confirm(
        "การกระทำนี้ไม่สามารถย้อนกลับได้ ยืนยันที่จะลบการสนทนาทั้งหมดหรือไม่?"
      );
      if (userConfirmed2) {
        onUpdateMessages([]);
      }
    }
  };

  // --- START: Suggested Questions (อัปเดตใหม่) ---
  const suggestedQuestions = [
    "สรุปประเด็นสำคัญทั้งหมดจากเนื้อหานี้",
    "ลิสต์สิ่งสำคัญที่ต้องทำ (Action Items) จากข้อมูลนี้",
    "มีข้อสรุปหรือการตัดสินใจอะไรที่สำคัญบ้าง?",
    "มีคำถามหรือประเด็นไหนที่ยังไม่ชัดเจน?",
  ];
  // --- END: Suggested Questions ---

  // --- START: Prompt Categories (อัปเดตใหม่ทั้งหมด) ---
  const categoryIcons = {
    สรุปภาพรวม: FiFileText,
    วิเคราะห์และเจาะลึก: FiSearch,
    ปรับรูปแบบและสร้างสรรค์: FiEdit3,
  };
  const promptCategories = {
    สรุปภาพรวม: [
      "สรุปเนื้อหาทั้งหมดแบบย่อ (Executive Summary)",
      "ใจความหลักของข้อมูลนี้คืออะไร?",
      "สรุปเนื้อหานี้ให้เหลือ 3-5 ข้อ",
      "เขียนสรุปในรูปแบบรายการ (bullet points)",
    ],
    วิเคราะห์และเจาะลึก: [
      "ลิสต์ Action Items หรือสิ่งที่ต้องทำต่อทั้งหมด",
      "ค้นหาและลิสต์ตัวเลขที่สำคัญทั้งหมดในเนื้อหา",
      "มีข้อขัดแย้งหรือความคิดเห็นที่ไม่ตรงกันในเนื้อหานี้หรือไม่?",
      "สรุปเฉพาะส่วนที่เกี่ยวกับ [ใส่หัวข้อ] ให้หน่อย",
    ],
    ปรับรูปแบบและสร้างสรรค์: [
      "เปลี่ยนเนื้อหานี้ให้เป็นรูปแบบอีเมล",
      "ร่างแผนงานจากข้อมูลนี้",
      "เขียนโพสต์สำหรับโซเชียลมีเดียจากเนื้อหานี้",
      "มีคำแนะนำหรือไอเดียใหม่ๆ ที่น่าสนใจหรือไม่?",
    ],
  };
  // --- END: Prompt Categories ---

  if (!transcript) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500">
        <p>อัปโหลดไฟล์เพื่อเริ่มสนทนา</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 border-b border-slate-200 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-baseline gap-2">
            <span>สนทนากับ "ยั่งยืน"</span>
            {activeAttachments.length > 0 && (
              <span className="text-sm font-normal text-slate-500">
                ({activeAttachments.length} ไฟล์แนบ)
              </span>
            )}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            ถามคำถามเพื่อให้ "ยั่งยืน" ช่วย 'ย่อ' ข้อมูลให้คุณ
          </p>
          {activeAttachments.length > 0 && (
            <p className="text-xs text-red-600 mt-1">
              หากมีไฟล์แนบ ยั่งยืนจะตอบช้าลง
            </p>
          )}
        </div>
        <div className="flex items-center gap-2" ref={menuRef}>
          {messages.length > 0 && (
            <button
              onClick={handleClearChat}
              className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
              title="ล้างการสนทนา"
            >
              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => {
              if (user?.can_use_podcast_feature) {
                setIsPodcastModalOpen(true);
              } else {
                alert('คุณไม่มีสิทธิ์ใช้ฟีเจอร์นี้');
              }
            }}
            className="p-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
            title="สร้าง Podcast สรุป"
          >
            <SafeIcon icon={FiMic} className="w-5 h-5" />
          </button>
          <div className="relative">
            <button
              onClick={() => setIsPromptMenuOpen(!isPromptMenuOpen)}
              className={`p-2 rounded-lg transition-colors ${
                isPromptMenuOpen
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 hover:bg-slate-200 text-slate-600"
              }`}
              title="คำสั่งลัด"
            >
              <SafeIcon icon={FiZap} className="w-5 h-5" />
            </button>
            <AnimatePresence>
              {isPromptMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }} // <-- อนิเมชันที่นุ่มนวลขึ้น
                  className="absolute top-full right-0 mt-2 w-80 origin-top-right bg-gradient-to-b from-white to-slate-50 rounded-xl shadow-2xl border border-slate-200 z-30"
                >
                  {/* --- Header ที่ออกแบบใหม่ --- */}
                  <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                    <FiZap className="w-5 h-5 text-green-600" />
                    <h3 className="font-bold text-slate-800 text-base">
                      ตัวช่วยสร้างคำถาม
                    </h3>
                  </div>

                  {/* --- Body ที่ออกแบบใหม่ --- */}
                  <div className="p-2 space-y-2 max-h-80 overflow-y-auto custom-scrollbar-thin">
                    {Object.entries(promptCategories).map(
                      ([category, prompts]) => (
                        <div key={category}>
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider px-2 pt-1 mb-1">
                            {category}
                          </h4>
                          <div className="space-y-1">
                            {prompts.map((prompt) => (
                              <button
                                key={prompt}
                                onClick={() => {
                                  setInputMessage(prompt);
                                  setIsPromptMenuOpen(false);
                                }}
                                // --- ดีไซน์ปุ่มแบบใหม่ที่มีไอคอน ---
                                className="group w-full text-left text-sm p-2.5 rounded-lg text-slate-700 hover:bg-green-50 hover:text-green-700 transition-colors flex items-center gap-3"
                              >
                                <div className="flex-shrink-0 bg-slate-100 group-hover:bg-green-100 p-1.5 rounded-md">
                                  <SafeIcon
                                    icon={categoryIcons[category] || FiZap} // <-- ใช้ไอคอนตาม Category
                                    className="w-4 h-4 text-slate-500 group-hover:text-green-600"
                                  />
                                </div>
                                <span>{prompt}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <PodcastModal
        isOpen={isPodcastModalOpen}
        onClose={() => setIsPodcastModalOpen(false)}
        transcript={transcript}
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/70">
        {messages.length === 0 && (
          <motion.div
            className="flex flex-col items-center text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.1 } }}
          >
            {/* 1. รูปภาพ Mascot */}
            <img
              src="/images/yy_chat.PNG"
              alt="ยั่งยืน AI พร้อมสนทนา"
              className="w-32 h-32 mb-4"
            />

            {/* 2. หัวข้อต้อนรับ */}
            <h3 className="text-xl font-bold text-slate-800">
              พร้อมให้ "ยั่งยืน" ช่วย'ย่อ'แล้ว!
            </h3>
            <p className="text-slate-500 mt-1 mb-6">
              ลองเริ่มต้นด้วยคำถามเหล่านี้ หรือพิมพ์คำถามของคุณเองได้เลย
            </p>

            {/* 3. ปุ่มคำถามแนะนำ */}
            <motion.div
              className="w-full max-w-md space-y-2"
              variants={{
                visible: {
                  transition: { staggerChildren: 0.08, delayChildren: 0.3 },
                },
              }}
              initial="hidden"
              animate="visible"
            >
              {suggestedQuestions.map((question, index) => (
                <motion.button
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  onClick={() => setInputMessage(question)}
                  className="group w-full text-left p-3 bg-white hover:bg-green-50 rounded-lg text-sm text-slate-700 hover:text-green-800 transition-all border border-slate-200 hover:border-green-300 shadow-sm hover:shadow-md flex items-center gap-3"
                >
                  <div className="flex-shrink-0 bg-slate-100 group-hover:bg-green-100 p-2 rounded-md">
                    <FiZap className="w-4 h-4 text-slate-500 group-hover:text-green-600" />
                  </div>
                  <span className="font-medium">{question}</span>
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        )}
        <AnimatePresence>
          {messages.map((messageItem, index) => {
            const isLastBotMessage =
              index === messages.length - 1 && messageItem.type === "bot";
            const isStreaming = isLastBotMessage && isLoading;

            let botAvatarSrc = null;
            if (messageItem.type === "bot") {
              const avatarNumber = (messageItem.id % 5) + 1;
              botAvatarSrc = `/images/yy_chat${avatarNumber}.PNG`;
            }

            return (
              <motion.div
                key={messageItem.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`flex items-start gap-3 ${
                  messageItem.type === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {messageItem.type === "bot" && (
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-200 overflow-hidden shadow-sm ring-2 ring-white">
                    <img
                      src={botAvatarSrc}
                      alt="Bot Avatar"
                      className="w-full h-full object-cover scale-110"
                    />
                  </div>
                )}
                <div
                  className={`max-w-[90%] p-3 rounded-xl relative group shadow-sm ${
                    messageItem.type === "user"
                      ? "bg-green-600 text-white"
                      : "bg-white text-slate-800 border border-slate-200"
                  }`}
                >
                  <div
                    className={`opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 absolute -top-4 ${
                      messageItem.type === "user" ? "right-2" : "left-2"
                    }`}
                  >
                    {messageItem.type === "bot" && !isStreaming && (
                      <>
                        <button
                          onClick={() =>
                            handleSaveAsSummary(
                              messageItem.content,
                              messageItem.id
                            )
                          }
                          className={`p-1.5 rounded-full shadow-sm border transition-colors duration-200 
                          ${
                            savedId === messageItem.id
                              ? "bg-green-100 text-slate-500 border-green-200"
                              : "bg-white text-slate-500 border-green-500 hover:bg-green-100 hover:border-green-200"
                          }
                        `}
                          title={
                            savedId === messageItem.id
                              ? "บันทึกแล้ว"
                              : "บันทึกเป็นสรุป"
                          }
                        >
                          <SafeIcon
                            icon={
                              savedId === messageItem.id ? FiCheck : FiBookmark
                            }
                            className="w-4 h-4"
                          />
                        </button>
                        <button
                          onClick={() =>
                            handleCopyMessage(
                              messageItem.content,
                              messageItem.id
                            )
                          }
                          className={`p-1.5 rounded-full shadow-sm border transition-colors duration-200 
                              ${
                                copiedId === messageItem.id
                                  ? "bg-green-100 text-slate-500 border-green-200"
                                  : "bg-white text-slate-500 border-green-500 hover:bg-green-100 hover:border-green-200"
                              }
                            `}
                          title={
                            copiedId === messageItem.id
                              ? "คัดลอกแล้ว"
                              : "คัดลอก"
                          }
                        >
                          <SafeIcon
                            icon={
                              copiedId === messageItem.id ? FiCheck : FiCopy
                            }
                            className="w-4 h-4"
                          />
                        </button>
                      </>
                    )}
                    {messageItem.type === "user" && (
                      <button
                        onClick={() =>
                          handleDeleteMessagePair(messageItem.id, index)
                        }
                        className="p-1.5 rounded-full bg-white hover:bg-red-500 text-slate-500 hover:text-white shadow-sm border border-slate-200"
                        title="ลบ"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div
                    className={`font-sans prose prose-lg max-w-none ${
                      messageItem.type === "user"
                        ? "prose-yungyoen-invert"
                        : "prose-yungyoen"
                    }`}
                  >
                    {isStreaming ? (
                      <AnimatedMarkdown
                        text={messageItem.content}
                        isStreaming={true}
                      />
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {messageItem.content}
                      </ReactMarkdown>
                    )}
                  </div>
                </div>
                {messageItem.type === "user" && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-green-700">
                    <SafeIcon icon={FiUser} className="w-5 h-5 text-white" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
        {isLoading &&
          !(
            messages.length > 0 &&
            messages[messages.length - 1].type === "bot"
          ) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start"
            >
              <div className="bg-white p-3 rounded-lg flex items-center gap-2 shadow-sm border border-slate-200">
                <SafeIcon
                  icon={FiLoader}
                  className="w-5 h-5 animate-spin text-green-600"
                />
                <span className="text-sm text-slate-600">กำลังคิด...</span>
              </div>
            </motion.div>
          )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="bg-red-100 text-red-700 p-3 rounded-lg flex items-center gap-2 shadow-sm border border-red-200">
              <FiIcons.FiAlertTriangle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {error} กรุณาลองอีกครั้ง
              </span>
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-2 border-t border-slate-200 bg-white">
        {/* Container หลักที่จัดวางทุกอย่าง */}
        <div className="relative flex items-end gap-2">
          {/* 1. Textarea ที่ขยายได้ */}
          <textarea
            ref={textareaRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="ถามคำถามของคุณที่นี่..."
            className="w-full p-3 pr-20 bg-slate-100 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none transition-all duration-200"
            rows={1}
            style={{ maxHeight: "100px" }} // จำกัดความสูงสูงสุด
          />

          {/* 2. ปุ่มอัดเสียง (อยู่ "ใน" textarea) */}
          <div className="absolute top-1/2 right-14 -translate-y-1/2">
            <VoiceInput onVoiceInput={handleVoiceMessage} />
          </div>

          {/* 3. ปุ่มส่งข้อความ */}
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="flex-shrink-0 w-11 h-11 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center justify-center"
            title="ส่งข้อความ"
          >
            <SafeIcon icon={FiSend} className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
